<script setup lang="ts">
import { ReservationStatus, ReservationStatusId } from '@previo/codebooks'
import { QIcon } from 'quasar'
import { computed } from 'vue'
import { $t } from '../../composables/i18n'
import PTooltip from '../tooltip/PTooltip.vue'
import { PReservationStatusIndicatorSize } from './types'

type Props = {
  reservationStatusId?: ReservationStatusId
  reservationStatusIds?: ReservationStatusId[]
  hideTooltip?: boolean
  size?: PReservationStatusIndicatorSize
}

const props = withDefaults(defineProps<Props>(), {
  hideTooltip: false,
  size: PReservationStatusIndicatorSize.MEDIUM,
})

const cssClassSize = computed<string>(() => `ds-reservation-status-indicator-${props.size}`)

const reservationStatus = computed<ReservationStatus | null>(() => {
  if (props.reservationStatusIds && props.reservationStatusIds.length) {
    return hasMultipleStatuses() ? null : getFirstReservationStatus()
  }

  return props.reservationStatusId ? ReservationStatus.getById(props.reservationStatusId) : null
})

const reservationStatuses = (): ReservationStatus[] =>
  Array.from(new Set(props.reservationStatusIds)).map((statusId) =>
    ReservationStatus.getById(statusId),
  )

const getNonCancelledStatuses = (): ReservationStatus[] =>
  reservationStatuses().filter((status: ReservationStatus) => !status.isCancelled)

const hasMultipleStatuses = (): boolean => {
  const uniqueNonCancelledStatuses = new Set(getNonCancelledStatuses())

  return uniqueNonCancelledStatuses.size > 0
    ? uniqueNonCancelledStatuses.size > 1
    : new Set(props.reservationStatusIds).size > 1
}

const getFirstReservationStatus = (): ReservationStatus => {
  const nonCancelledStatuses = getNonCancelledStatuses(),
    uniqueNonCancelledStatuses = new Set(getNonCancelledStatuses())

  return uniqueNonCancelledStatuses.size > 0
    ? nonCancelledStatuses[0]
    : ReservationStatus.getById((props.reservationStatusIds ?? [])[0])
}
</script>

<template>
  <template v-if="reservationStatus === null">
    <p-tooltip :text="hideTooltip ? null : $t('More statuses')">
      <q-icon
        class="ds-reservation-status-indicator ds-reservation-status-indicator-icon"
        :class="cssClassSize"
        name="fa-light fa-question"
      />
    </p-tooltip>
  </template>
  <template v-else>
    <p-tooltip :text="hideTooltip ? null : reservationStatus.nameCapitalize()">
      <q-icon
        v-if="reservationStatus.icon"
        class="ds-reservation-status-indicator ds-reservation-status-indicator-icon"
        :class="cssClassSize"
        :name="`fa-light fa-${reservationStatus.icon}`"
        :color="reservationStatus.color"
      />
      <div
        v-else
        class="ds-reservation-status-indicator ds-reservation-status-indicator-dot"
        :class="[cssClassSize, `bg-` + reservationStatus.color]"
      ></div>
    </p-tooltip>
  </template>
</template>

<style lang="scss" scoped>
.ds-reservation-status-indicator {
  width: 16px;
  height: 16px;

  &-icon {
    display: flex;
  }

  &-dot {
    border-radius: 50%;
  }

  &-small {
    width: 8px;
    height: 8px;
    font-size: 8px;
  }
}
</style>
